import React from 'react';
import { cn, Flex, hashStringToTagColor, Sparkline, TagBadge, Text } from '@chatsdet/ui';
import { Flag } from '@phosphor-icons/react';
import type { ProductItem } from '../types';
import { PRIORITY_COLORS, PRIORITY_LABELS } from '../constants';

/** Compact preview of the card's primary linked metric (from the batched board query). */
export interface CardMetricPreview {
  metricName: string;
  unit: string | null;
  values: number[];
}

interface Props {
  item: ProductItem;
  isOverlay?: boolean;
  deprecated?: boolean;
  /** Primary metric sparkline, when this card has a linked metric with data. */
  metricPreview?: CardMetricPreview;
}

export function ProductItemCard({ item, isOverlay, deprecated, metricPreview }: Props) {
  const customers = item.customers ?? [];

  return (
    <div
      className={cn(
        'px-3 py-2.5 border border-border bg-card shadow-sm',
        'transition-all duration-150',
        !isOverlay && 'hover:shadow-md',
        deprecated && 'opacity-70',
        isOverlay && 'border-primary/30'
      )}
    >
      <Flex gap="xs" align="start" justify="between">
        <Text size="sm" weight="medium" className="leading-tight line-clamp-2 min-w-0">
          {item.title}
        </Text>
        {item.flagDriven && (
          <Flag
            size={13}
            weight="fill"
            className="mt-0.5 shrink-0 text-muted-foreground"
            aria-label="Stage driven by feature flags"
          />
        )}
        {item.priority && (
          <TagBadge
            name={PRIORITY_LABELS[item.priority]}
            color={PRIORITY_COLORS[item.priority]}
            hideDot
            size="sm"
            className="shrink-0"
          />
        )}
      </Flex>
      {item.description && (
        <Text size="xs" className="text-muted-foreground/80 line-clamp-3 mt-1">
          {item.description}
        </Text>
      )}
      {metricPreview && metricPreview.values.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5" title={metricPreview.metricName}>
          <Sparkline points={metricPreview.values} width={64} height={16} showLastDot={false} />
          <Text size="xs" className="text-muted-foreground">
            {metricPreview.values[metricPreview.values.length - 1]}
            {metricPreview.unit ? ` ${metricPreview.unit}` : ''}
          </Text>
        </div>
      )}
      {customers.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {customers.slice(0, 2).map((customer) => (
            <TagBadge
              key={customer.id}
              name={customer.name}
              color={hashStringToTagColor(customer.name)}
              size="sm"
            />
          ))}
          {customers.length > 2 && (
            <Text size="xs" className="text-muted-foreground leading-6">
              +{customers.length - 2}
            </Text>
          )}
        </div>
      )}
    </div>
  );
}
